import { CartLinesUpdateEvent } from '@shopify/events';

class QuickCrossSellCard extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.selects = [...this.querySelectorAll('select[data-option-index]')];
    this.button = this.querySelector('[data-add]');
    this.message = this.querySelector('[data-message]');
    this.selects.forEach((select) => select.addEventListener('change', () => this.updateVariant()));
    this.button?.addEventListener('click', () => this.addToCart());
    this.updateVariant();
    requestAnimationFrame(() => {
      const mainColor = document.querySelector(
        'variant-picker[data-template-product-match="true"] input[type="radio"][name^="Color-"]:checked, variant-picker[data-template-product-match="true"] select[name*="[Color]"]'
      );
      if (mainColor) this.syncColor(mainColor.value);
    });
  }
  get selectedVariant() {
    const options = this.selects.map((select) => select.value);
    return this.variants.find((variant) => variant.options.every((value, index) => value === options[index])) || null;
  }
  normalizeOption(value) {
    return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
  }
  normalizeColor(value) {
    const normalized = this.normalizeOption(value).replace(/^[a-z0-9]+\s*-\s*/, '');
    const aliases = {
      'verde botella': 'hunter',
      'verde caribe': 'azul caribe'
    };
    return aliases[normalized] || normalized;
  }
  syncColor(mainColor) {
    const colorSelect = this.selects.find((select) => /color/i.test(select.getAttribute('aria-label') || ''));
    if (!colorSelect) return;

    const normalizedColor = this.normalizeColor(mainColor);
    const matchingOption = [...colorSelect.options].find(
      (option) => this.normalizeColor(option.value) === normalizedColor
    );
    if (!matchingOption) return;

    colorSelect.value = matchingOption.value;

    let variant = this.selectedVariant;
    if (!variant?.available) {
      const colorIndex = Number(colorSelect.dataset.optionIndex);
      variant = this.variants.find(
        (item) => item.available && this.normalizeColor(item.options[colorIndex]) === normalizedColor
      );
      if (variant) {
        this.selects.forEach((select, index) => {
          if (variant.options[index] != null) select.value = variant.options[index];
        });
      }
    }

    this.updateVariant();
  }
  syncSize(mainSize) {
    const sizeSelect = this.selects.find((select) =>
      /talla|size/i.test(select.getAttribute('aria-label') || '')
    );
    if (!sizeSelect) return;

    const normalizedSize = this.normalizeOption(mainSize);
    const matchingOption = [...sizeSelect.options].find(
      (option) => this.normalizeOption(option.value) === normalizedSize
    );

    if (!matchingOption) {
      this.button.disabled = true;
      this.button.textContent = 'Talla no disponible';
      this.message.textContent = `Talla ${mainSize} no disponible en este producto`;
      this.message.className = 'quick-cross-sell-card__message is-error';
      return;
    }

    sizeSelect.value = matchingOption.value;
    this.updateVariant();
  }
  updateVariant() {
    const variant = this.selectedVariant;
    const price = this.querySelector('[data-price]');
    const image = this.querySelector('.quick-cross-sell-card__image');
    if (!variant) {
      this.button.disabled = true;
      this.button.textContent = 'Combinación no disponible';
      return;
    }
    if (price) price.textContent = this.formatMoney(variant.price);
    if (image) {
      const colorSelect = this.selects.find((select) => /color/i.test(select.getAttribute('aria-label') || ''));
      const colorIndex = colorSelect ? Number(colorSelect.dataset.optionIndex) : 0;
      const colorValue = colorSelect?.value;
      const imageVariant = variant.featured_image?.src
        ? variant
        : this.variants.find((item) => item.options[colorIndex] === colorValue && item.featured_image?.src);
      if (imageVariant?.featured_image?.src) {
        const imageUrl = new URL(imageVariant.featured_image.src, window.location.origin);
        imageUrl.searchParams.set('width', '360');
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = imageUrl.toString();
        image.alt = `${this.dataset.productHandle || 'Producto'} - color ${colorValue || ''}`;
      }
    }
    this.button.disabled = !variant.available;
    this.button.textContent = variant.available ? 'Agregar al carrito' : 'Agotado';
    this.message.textContent = '';
    this.message.className = 'quick-cross-sell-card__message';
  }
  formatMoney(cents) {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(cents / 100);
  }
  async addToCart() {
    const variant = this.selectedVariant;
    if (!variant?.available) return;
    this.button.disabled = true;
    this.button.textContent = 'Agregando…';
    const cartItemsComponents = [...document.querySelectorAll('cart-items-component')];
    const sectionIds = cartItemsComponents
      .map((item) => item instanceof HTMLElement ? item.dataset.sectionId : null)
      .filter(Boolean);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variant.id), quantity: 1 }],
      promise: deferredEventPromise.promise
    }));

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variant.id, quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname
        })
      });
      const added = await response.json();
      if (!response.ok) throw new Error(added.description || 'No se pudo agregar esta variante.');

      const cart = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      }).then((result) => result.json());

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: added.sections,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'quick-cross-sell',
          sourceId: this.dataset.productHandle || 'quick-cross-sell',
          variantId: String(variant.id),
          didError: false
        }
      });
      this.message.textContent = 'Agregado al carrito';
      this.message.classList.add('is-success');
      this.button.textContent = 'Agregado ✓';
      setTimeout(() => { this.button.disabled = false; this.button.textContent = 'Agregar al carrito'; }, 1800);
    } catch (error) {
      deferredEventPromise.reject(error);
      this.message.textContent = error.message || 'Ocurrió un error. Intenta nuevamente.';
      this.message.classList.add('is-error');
      this.button.disabled = false;
      this.button.textContent = 'Agregar al carrito';
    }
  }
}
if (!customElements.get('quick-cross-sell-card')) customElements.define('quick-cross-sell-card', QuickCrossSellCard);

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
  if (target.closest('quick-cross-sell-card')) return;

  const picker = target.closest('variant-picker[data-template-product-match="true"]');
  if (!picker || target.closest('product-card, quick-add-dialog')) return;

  const fieldsetLabel = target.closest('fieldset')?.querySelector('legend')?.textContent || '';
  const selectLabel = target.closest('.variant-option--dropdowns')?.querySelector('label')?.textContent || '';
  const optionName = target.name?.match(/options\[([^\]]+)\]/)?.[1] || fieldsetLabel || selectLabel || target.name;
  const cards = document.querySelectorAll('.quick-cross-sell quick-cross-sell-card');

  if (/color/i.test(optionName || '')) {
    cards.forEach((card) => card.syncColor?.(target.value));
  } else if (/talla|size/i.test(optionName || '')) {
    cards.forEach((card) => card.syncSize?.(target.value));
  }
});


class QuickCrossSellCarousel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.carouselReady) return;
    this.dataset.carouselReady = 'true';
    this.viewport = this.querySelector('[data-carousel-viewport]');
    this.prevButton = this.querySelector('[data-carousel-prev]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    if (!this.viewport || !this.prevButton || !this.nextButton) return;

    this.prevButton.addEventListener('click', () => this.move(-1));
    this.nextButton.addEventListener('click', () => this.move(1));
    this.viewport.addEventListener('scroll', () => this.queueUpdate(), { passive: true });
    new ResizeObserver(() => this.updateControls()).observe(this.viewport);
    requestAnimationFrame(() => this.updateControls());
  }

  get step() {
    const card = this.viewport.querySelector('.quick-cross-sell-card');
    if (!card) return this.viewport.clientWidth;
    const grid = card.parentElement;
    const gap = Number.parseFloat(getComputedStyle(grid).gap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  move(direction) {
    this.viewport.scrollBy({ left: direction * this.step, behavior: 'smooth' });
  }

  queueUpdate() {
    cancelAnimationFrame(this.updateFrame);
    this.updateFrame = requestAnimationFrame(() => this.updateControls());
  }

  updateControls() {
    const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
    this.prevButton.disabled = this.viewport.scrollLeft <= 2;
    this.nextButton.disabled = this.viewport.scrollLeft >= maxScroll - 2;
  }
}
if (!customElements.get('quick-cross-sell-carousel')) customElements.define('quick-cross-sell-carousel', QuickCrossSellCarousel);
